import { AccountType } from "shared/types/accounts";
import { CalendarPostType } from "features/calendar-new/types";
import {
  getAccountNameParts,
  getFirstGroupOrAmountOfAccountsIds,
  sortAccounts,
} from "utils/accounts";
import { accountHasGroup } from "../libs/accounts-groups";

export function deriveSelectedAccountIds(
  accounts: AccountType[],
  selectedAccountIds: string[]
): string[] {
  if (!selectedAccountIds.length) {
    return getFirstGroupOrAmountOfAccountsIds(sortAccounts(accounts), 5);
  }

  const accountIds = new Set(accounts.map((account) => account._id));

  return selectedAccountIds.filter((accountId) => accountIds.has(accountId));
}

export function buildPostCreatedSelectionUpdate({
  accounts,
  selectedAccountIds,
  newPosts,
}: {
  accounts: AccountType[];
  selectedAccountIds: string[];
  newPosts: CalendarPostType[];
}): {
  nextSelectedAccountIds: string[];
  homeSelectedGroupName: string | null;
} {
  const nextSelectedAccountIds = [...selectedAccountIds];
  const accountsToAdd: string[] = [];

  newPosts.forEach((post) => {
    post.accountIds?.forEach((accountId) => {
      if (
        !nextSelectedAccountIds.includes(accountId) &&
        !accountsToAdd.includes(accountId)
      ) {
        accountsToAdd.push(accountId);
      }
    });
  });

  if (accountsToAdd.length > 0) {
    nextSelectedAccountIds.push(...accountsToAdd);
  }

  const firstAccountWithGroupToAdd = accounts.find(
    (account) => accountsToAdd.includes(account._id) && accountHasGroup(account)
  );

  if (!firstAccountWithGroupToAdd) {
    return {
      nextSelectedAccountIds,
      homeSelectedGroupName: null,
    };
  }

  const [groupName] = getAccountNameParts(firstAccountWithGroupToAdd);

  return {
    nextSelectedAccountIds,
    homeSelectedGroupName: groupName,
  };
}

export function deriveSelectedGroupNameForIdeasNavigation(
  accounts: AccountType[]
): string | null {
  if (!accounts.length) {
    return null;
  }

  const groupNames = new Set<string>();

  for (const account of accounts) {
    if (!accountHasGroup(account)) {
      return null;
    }

    // Grouped accounts can still have legacy aliases that fail to parse cleanly.
    // Treat those the same as ungrouped selections instead of guessing.
    const [groupName] = getAccountNameParts(account);

    if (!groupName) {
      return null;
    }

    groupNames.add(groupName);

    if (groupNames.size > 1) {
      return null;
    }
  }

  return groupNames.values().next().value ?? null;
}

export function deriveStoredGroupNameForIdeasNavigation(
  accounts: AccountType[]
): string {
  return deriveSelectedGroupNameForIdeasNavigation(accounts) ?? "";
}
